import {
    resolveWorkflowStateChain,
    type ResolveResult,
} from '@toolproof-core/lib/_pure/workflow-state-resolution';
import type {
    MutableMaterializedResource,
    MutableToolStepRoleAddress,
    MutableWorkflowState,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import { toProjectionText } from '@/workflow/_pure/parsing';

export type WorkflowStateEntryResolutionResult = {
    entry: MutableMaterializedResource | null;
    isFilled: boolean;
    projectionText: string | null;
};

export function resolveWorkflowStateEntryResolution(args: {
    direction: 'input' | 'output';
    stepKey: string;
    roleName: string;
    workflowState: MutableWorkflowState;
    isInputFilled?: (args: {
        stepKey: string;
        roleName: string;
        workflowState: MutableWorkflowState;
        resolved: ResolveResult;
        inputEntry: MutableMaterializedResource | null;
    }) => boolean;
    isOutputFilled?: (args: {
        stepKey: string;
        roleName: string;
        workflowState: MutableWorkflowState;
        outputEntry: MutableMaterializedResource | null;
    }) => boolean;
}): WorkflowStateEntryResolutionResult {
    const { direction, stepKey, roleName, workflowState } = args;

    if (direction === 'output') {
        const outputEntry = (workflowState as Record<string, Record<string, MutableMaterializedResource | undefined>>)[stepKey]?.[roleName];
        const entry = outputEntry?.workflowStateEntryKind === 'materializedResource'
            ? outputEntry
            : null;

        return {
            entry,
            isFilled: args.isOutputFilled
                ? args.isOutputFilled({
                    stepKey,
                    roleName,
                    workflowState,
                    outputEntry: entry,
                })
                : entry !== null,
            projectionText: toProjectionText(entry),
        };
    }

    const targetAddress: MutableToolStepRoleAddress = {
        stepKey: stepKey as MutableToolStepRoleAddress['stepKey'],
        roleName: roleName as MutableToolStepRoleAddress['roleName'],
    };
    const resolved = resolveWorkflowStateChain(workflowState, targetAddress);
    const entry = resolved.status === 'materializedResource'
        ? resolved.entry
        : null;

    return {
        entry,
        isFilled: args.isInputFilled
            ? args.isInputFilled({
                stepKey,
                roleName,
                workflowState,
                resolved,
                inputEntry: entry,
            })
            : resolved.status === 'materializedResource',
        projectionText: toProjectionText(entry),
    };
}
